import React, { useState } from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { Button, Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { Alert, Col, FormGroup, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Loader } from 'app/shared/loader/loader';
import { fetchFormationCommitmentCodebookQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { NumericFormat } from 'react-number-format';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { fetchCaseBuildingPlotQuery, fetchDkpLocationsQuery, saveCaseBuildingPlotQuery } from './service/case-building-plot-form.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import CaseBuildingPlotBeingCreatedForm from 'app/edozvola/cases/case-building-plot/case-building-plot-being-created-form';
import CaseBuildingPlotBeingCreatedTable from './case-building-plot-being-created-table';
import { ToggleWithLabels } from 'edozvola-frontend-lib/dist/ui/components/toggle-with-labels';
import { CaseBuildingPlotModel } from 'app/edozvola/cases/case-building-plot/model/case-building-plot-form.model';
import { caseBuildingPlotValidationSchema } from 'app/edozvola/cases/case-building-plot/model/case-building-plot-form.schema';
import { getCaseContext } from 'app/shared/util/context-util';

const CaseBuildingPlotForm = () => {
  const { id } = useParams<{ id: string }>();

  const [idCaseBuildingPlotBeingCreated, setIdCaseBuildingPlotBeingCreated] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);

  const { data: initialData, isLoading: initialDataLoading } = fetchCaseBuildingPlotQuery(id);
  const { data: formationCommitmentOptions, isLoading: formationCommitmentOptionsLoading } = fetchFormationCommitmentCodebookQuery();
  const { data: dkpLocations, isLoading: dkpLocationsLoading } = fetchDkpLocationsQuery(id);

  const { mutate: saveCaseBuildingPlotMutation, isPending: isCaseBuildingPlotSaving } = saveCaseBuildingPlotQuery(id);
  const dispatch = useDispatch();

  const { sidebarItemPair } = getCaseContext();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData || ({} as CaseBuildingPlotModel),
    validationSchema: caseBuildingPlotValidationSchema,
    validateOnMount: true,
    onSubmit: values => saveCaseBuildingPlotMutation(values),
  });

  const logicDataFalseOrTrueAndNotSaved = () => {
    return (formik.dirty && !initialData.plotBeingFormed && formik.values.plotBeingFormed) || !formik.values.plotBeingFormed;
  };

  const openNewPlotBeingCreatedForm = () => {
    setIdCaseBuildingPlotBeingCreated('');
    setReadOnly(false);
  };

  const plotBeingFormedChanged = event => {
    formik.setValues({
      ...formik.values,
      plotBeingFormed: event.target.checked,
      formationCommitment: null,
      plotRatio: null,
    });
  };

  const openIspuLocator = () => {
    const url = window.location.origin + `/ispu-locator/case-building-plot/${id}`;

    window.open(url, '_blank');
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form>
          <FormikValueObserver
            onChange={dirty => {
              if (dirty.dirty) {
                dispatch(setFormChanged(true));
              } else {
                dispatch(initCaseFormChange());
              }
            }}
          />
          {initialDataLoading && <Loader />}
          {!initialDataLoading && (
            <div>
              <Row>
                <Label labelText={'Građevna čestica se formira'} />
                <ToggleWithLabels
                  trueLabel="DA"
                  falseLabel="NE"
                  checked={formik.values.plotBeingFormed}
                  onChange={plotBeingFormedChanged}
                  disabled={sidebarItemPair?.readOnly}
                />
              </Row>
              {formik.dirty && !initialData?.plotBeingFormed && formik.values.plotBeingFormed && (
                <Row>
                  <Col md={6}>
                    <Alert color="warning">
                      Potrebno je prvo spremiti obvezne podatke na kartici da biste mogli dodati građevne čestice!
                    </Alert>
                  </Col>
                </Row>
              )}
              <Row>
                <Col xs={6}>
                  <FormGroup>
                    <Label labelText={'Obveza formiranja građevne čestice'} />
                    <Field
                      id="formationCommitment"
                      name="formationCommitment"
                      render={({ field }) => (
                        <PaginatedDropdown
                          placeholder={
                            !sidebarItemPair?.readOnly && formik.values.plotBeingFormed && 'Odaberi obvezu formiranja građevne čestice'
                          }
                          onItemSelect={selectedOption => formik.setFieldValue('formationCommitment', selectedOption[0])}
                          selected={field?.value ?? []}
                          labelKey={'value'}
                          options={formationCommitmentOptionsLoading ? [] : formationCommitmentOptions}
                          disabled={sidebarItemPair?.readOnly || !formik.values.plotBeingFormed}
                          error={!sidebarItemPair?.readOnly && (formik.errors.formationCommitment as string)}
                          showErrorMessage
                        />
                      )}
                    />
                  </FormGroup>
                </Col>
              </Row>
              {!sidebarItemPair?.readOnly && (
                <>
                  <hr />
                <div className={'d-flex justify-content-between'}>
                  <div className={'form-header'}>Građevne čestice koje se formiraju</div>
                  <div>
                    <Button
                      label={'Dodaj česticu'}
                      color={'info'}
                      prependIcon={'plus'}
                      size={'lg'}
                      disabled={logicDataFalseOrTrueAndNotSaved()}
                      onClick={openNewPlotBeingCreatedForm}
                    />
                  </div>
                </div>
                </>
              )}
              <CaseBuildingPlotBeingCreatedTable
                setPlotBeingCreated={setIdCaseBuildingPlotBeingCreated}
                setReadOnly={setReadOnly}
                plotBeingFormedMarkedAsTrueAndSaved={!logicDataFalseOrTrueAndNotSaved()}
              />
              {idCaseBuildingPlotBeingCreated !== null && (
                <CaseBuildingPlotBeingCreatedForm
                  idCaseBuildingPlotBeingCreated={idCaseBuildingPlotBeingCreated}
                  setIdCaseBuildingPlotBeingCreated={setIdCaseBuildingPlotBeingCreated}
                  readOnly={readOnly}
                  dkpLocations={dkpLocations}
                />
              )}
              <Row>
                <Col md={6}>
                  <Label labelText={'Građevna čestica (*.gml)'} />
                  <div className="d-flex ispu-input-wrapper">
                    <Input
                      disabled={true}
                      placeholder={formik.values?.geometryExists ? 'Spremljena je GML datoteka.' : 'Ne postoji spremljena GML datoteka.'}
                    />
                    <Button disabled={logicDataFalseOrTrueAndNotSaved()} label="ISPU lokator" onClick={openIspuLocator} color="info" />
                  </div>
                </Col>
                <Col xs={6}>
                  <FormGroup>
                    <Label labelText={'Izgrađenost građevne čestice'} />
                    <Field
                      id="plotRatio"
                      name="plotRatio"
                      render={({ field }) => (
                        <>
                          <div className="d-flex align-items-center border rounded">
                            <NumericFormat
                              decimalScale={2}
                              allowNegative={false}
                              thousandSeparator="."
                              decimalSeparator=","
                              value={field.value || ''}
                              customInput={Input}
                              onValueChange={values => formik.setFieldValue('plotRatio', values.floatValue)}
                              disabled={sidebarItemPair?.readOnly || !formik.values.plotBeingFormed}
                              className={
                                !sidebarItemPair?.readOnly && formik.errors.plotRatio ? 'is-invalid' : 'flex-grow-1 border-0 shadow-none'
                              }
                            />
                            <span className="ms-2 me-2 text-muted">%</span>
                          </div>
                          {!sidebarItemPair?.readOnly && formik.errors.plotRatio && (
                            <div className="invalid-feedback order-last">{formik.errors.plotRatio as string}</div>
                          )}
                        </>
                      )}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {!sidebarItemPair?.readOnly && (
                <div className="case-save-button">
                  <Button
                    color={'info'}
                    label={'Spremi'}
                    isLoading={isCaseBuildingPlotSaving}
                    size={'lg'}
                    onClick={formik.submitForm}
                    disabled={!formik.dirty || !formik.isValid}
                  />
                </div>
              )}
            </div>
          )}
        </Form>
      </FormikProvider>
    </>
  );
};

export default CaseBuildingPlotForm;
